import {Injectable} from '@angular/core';
import {CompanyService} from '../company/company.service';
import {TeamService} from "../team/team.service";
import {ApiResponse} from '../utils';
import {PostModel} from '../../models/post.model';
import {TeamModel} from '../../models/team.model';
import {UserModel} from '../../models/user.model';

interface DataState<T> {
  items: T[];
  page: number;
  loading: boolean;
  maxDataLoaded: boolean;
}

@Injectable({providedIn: 'root'})
export class SidePanelDataService {

  // Company state
  posts: DataState<PostModel> = this.initState<PostModel>();
  actionPoints: DataState<PostModel> = this.initState<PostModel>();
  teams: DataState<TeamModel> = this.initState<TeamModel>();
  followers: DataState<UserModel> = this.initState<UserModel>();
  employees: DataState<UserModel> = this.initState<UserModel>();

  // Space (Team) state
  spacePosts: DataState<PostModel> = this.initState<PostModel>();
  spaceMembers: DataState<UserModel> = this.initState<UserModel>();
  spaceActionPoints: DataState<PostModel> = this.initState<PostModel>();

  constructor(
    private companyService: CompanyService,
    private teamService: TeamService
  ) {}

  private initState<T>(): DataState<T> {
    return {
      items: [],
      page: 1,
      loading: false,
      maxDataLoaded: false,
    };
  }

  public loadData<T>(
    id: number | string,
    state: DataState<T>,
    apiCall: (id: number | string, page: number) => any
  ): void {
    if (state.loading || state.maxDataLoaded) return;

    state.loading = true;

    apiCall(id, state.page).subscribe((result: ApiResponse): void => {
      if (result.status) {
        const items: T[] = result.data.data;
        if (state.page === 1) {
          state.items = items;
        } else if (items.length === 0) {
          state.maxDataLoaded = true;
        } else {
          state.items.push(...items);
        }
        state.page++;
      }
      state.loading = false;
    });
  }

  // Company loaders
  public loadCompanyPosts(companyId: number | string): void {
    this.loadData(companyId, this.posts, this.companyService.apiCompanyPostsList.bind(this.companyService));
  }

  public loadCompanyActionPoints(companyId: number | string): void {
    this.loadData(companyId, this.actionPoints, this.companyService.apiCompanyActionPointsList.bind(this.companyService));
  }

  public loadCompanyTeams(companyId: number | string): void {
    this.loadData(companyId, this.teams, this.companyService.apiCompanyTeamsList.bind(this.companyService));
  }

  public loadCompanyFollowers(companyId: number | string): void {
    this.loadData(companyId, this.followers, this.companyService.apiCompanyFollowerList.bind(this.companyService));
  }

  public loadCompanyEmployees(companyId: number | string): void {
    this.loadData(companyId, this.employees, this.companyService.apiGetCompanyEmployees.bind(this.companyService));
  }

  // Space (Team) loaders
  public loadSpacePosts(spaceId: number | string): void {
    this.loadData(spaceId, this.spacePosts, this.teamService.apiGetTeamPost.bind(this.teamService));
  }

  public loadSpaceMembers(spaceId: number | string): void {
    this.loadData(spaceId, this.spaceMembers, this.teamService.apiTeamMembers.bind(this.teamService));
  }

  public loadSpaceActionPoints(spaceId: number | string): void {
    this.loadData(spaceId, this.spaceActionPoints, this.teamService.apiGetTeamAPs.bind(this.teamService));
  }

  // Reset all states
  public reset(): void {
    this.posts = this.initState<PostModel>();
    this.actionPoints = this.initState<PostModel>();
    this.teams = this.initState<TeamModel>();
    this.followers = this.initState<UserModel>();
    this.employees = this.initState<UserModel>();
    this.spacePosts = this.initState<PostModel>();
    this.spaceMembers = this.initState<UserModel>();
    this.spaceActionPoints = this.initState<PostModel>();
  }
}
